import { useState } from "react";
import { allOptionId, resolveSelectionWithAll } from "./RiskSettings.utils";

export interface SelectableCatalogRow {
    entry: number;
    name: string;
    description?: string;
    categoryLabel: string;
    isSelected: boolean;
}

export function useCatalogEntrySelection() {
    const [selectedEntries, setSelectedEntries] = useState<number[]>([]);

    const toggleEntry = (entry: number) => {
        setSelectedEntries((current) => (current.includes(entry) ? current.filter((x) => x !== entry) : [...current, entry]));
    };

    const isAllSelected = (allEntries: number[]) => allEntries.every((entry) => selectedEntries.includes(entry));

    const onSelectAllChanged = (allEntries: number[]) => {
        setSelectedEntries(isAllSelected(allEntries) ? [] : allEntries);
    };

    const getHeaderCheckBoxState = (allEntries: number[]): "true" | "false" | "indeterminate" => {
        if (selectedEntries.length === 0) return "false";
        return isAllSelected(allEntries) ? "true" : "indeterminate";
    };

    return { selectedEntries, toggleEntry, onSelectAllChanged, getHeaderCheckBoxState, hasSelection: selectedEntries.length > 0 };
}

// Manages a per-entry multi-select where an empty stored selection means "applies to all", shown as the "All" chip.
export function useSelectionWithAllByEntry() {
    const [valuesByEntry, setValuesByEntry] = useState<Record<number, string[]>>({});

    const getChosen = (entry: number) => {
        const selection = valuesByEntry[entry] ?? [];
        return selection.length > 0 ? selection : [allOptionId];
    };

    const set = (entry: number, values: string[]) => {
        const selection = resolveSelectionWithAll(values, getChosen(entry));
        setValuesByEntry((current) => ({ ...current, [entry]: selection.includes(allOptionId) ? [] : selection }));
    };

    return { valuesByEntry, getChosen, set };
}
